import { batch } from 'react-redux';

// project imports
import gestopAPI from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { commonDepartmentActions } from './slice';
import capabilities from './capabilities';
import createImportApi from 'utils/importAPI';

const departmentImportApi = createImportApi({
    actions: commonDepartmentActions,
    capabilities,
    endpoint: 'common/departments',
    templateFileName: 'DepartmentImportTpl'
});
export const ApiDepartmentsImportCollection = departmentImportApi.importCollection;
export const ApiDepartmentsDownloadImportModelItem = departmentImportApi.downloadImportModel;

export const ApiDepartmentsGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonDepartmentActions.startLoading());
    }

    await gestopAPI
        .post('common/departments/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonDepartmentActions.setList(response?.data?.list));
                    dispatch(commonDepartmentActions.setCount(response?.data?.count));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data?.list);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonDepartmentActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiDepartmentsCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('common/departments/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiDepartmentsPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonDepartmentActions.startLoading());
    }

    await gestopAPI
        .post('common/departments', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonDepartmentActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonDepartmentActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiDepartmentsDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonDepartmentActions.startLoading());
    }

    await gestopAPI
        .post('common/departments/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonDepartmentActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonDepartmentActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiDepartmentsExportCollection = async ({
    exportType = 'allRows',
    format = 'pdf',
    columns = [],
    filters = [],
    orderBy = [],
    groupBy = [],
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(commonDepartmentActions.startExporting());
    }

    await gestopAPI
        .post('common/departments/export', { exportType, format, columns, filters, orderBy, groupBy }, { ...config, responseType: 'blob' })
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(commonDepartmentActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(commonDepartmentActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonDepartmentActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiDepartmentsPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonDepartmentActions.startLoading());
    }

    await gestopAPI
        .put(`common/departments/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonDepartmentActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonDepartmentActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiDepartmentsPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonDepartmentActions.startLoading());
    }

    await gestopAPI
        .patch(`common/departments/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonDepartmentActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonDepartmentActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiDepartmentsDeleteItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonDepartmentActions.startLoading());
    }

    await gestopAPI
        .post(`common/departments/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonDepartmentActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonDepartmentActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiDepartmentsRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonDepartmentActions.startLoading());
    }

    await gestopAPI
        .post(`common/departments/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(commonDepartmentActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonDepartmentActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiDepartmentsGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(commonDepartmentActions.startLoading());
    }

    await gestopAPI
        .get(`common/departments/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(commonDepartmentActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(commonDepartmentActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};
